/**
 * InvoiceFormAdditional
 *
 * "Additional Information" section: payment terms, bank account selection,
 * notes, and terms & conditions.
 */

import { PAYMENT_TERM_OPTIONS , creditDaysToTermString } from './invoice-form-helpers'
import { Input, FormField, Select, Textarea } from '../../ui'

import type { UseInvoiceFormReturn } from './useInvoiceForm'

interface InvoiceFormAdditionalProps {
  form: UseInvoiceFormReturn
  isVendor: boolean
  isLoading: boolean
}

function buildBankLabel(acc: UseInvoiceFormReturn['bankAccounts'][number], isExport: boolean): string {
  const base = acc.bankName && acc.accountNumber
    ? `${acc.bankName} - ****${acc.accountNumber.slice(-4)} (${acc.currency})`
    : `${acc.accountName} (${acc.currency})`
  return isExport && acc.currency === 'INR' ? `${base} — domestic only` : base
}

function sortExportBankAccounts(bankAccounts: UseInvoiceFormReturn['bankAccounts'], isExport: boolean): UseInvoiceFormReturn['bankAccounts'] {
  if (!isExport) { return bankAccounts }
  return [...bankAccounts].sort((a, b) => {
    const aInt = a.currency !== 'INR' ? -1 : 1
    const bInt = b.currency !== 'INR' ? -1 : 1
    return aInt - bInt
  })
}

interface PaymentTermsRowProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
}

function PaymentTermsRow({ form, isLoading }: PaymentTermsRowProps): JSX.Element {
  const { selectedPaymentTermValue, setSelectedPaymentTermValue, customCreditDays, setCustomCreditDays, setPaymentTerms } = form
  return (
    <>
      <FormField hideMessage label="Payment Terms">
        <Select
          disabled={isLoading}
          options={PAYMENT_TERM_OPTIONS}
          value={selectedPaymentTermValue}
          onChange={(val) => {
            setSelectedPaymentTermValue(val)
            if (val !== 'custom') {
              setCustomCreditDays('')
              setPaymentTerms(creditDaysToTermString(parseInt(val, 10) || 0))
            }
          }}
        />
      </FormField>
      {selectedPaymentTermValue === 'custom' && (
        <FormField hideMessage label="Credit Days">
          <Input
            disabled={isLoading}
            min="0"
            placeholder="Enter days"
            type="number"
            value={customCreditDays}
            onChange={(e) => {
              setCustomCreditDays(e.target.value)
              setPaymentTerms(creditDaysToTermString(parseInt(e.target.value, 10) || 0))
            }}
          />
        </FormField>
      )}
    </>
  )
}

interface BankAccountSelectProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
  isExport: boolean
}

function BankAccountSelect({ form, isLoading, isExport }: BankAccountSelectProps): JSX.Element {
  const { bankAccounts, bankGLAccounts, selectedBankAccountId, setSelectedBankAccountId } = form
  const sortedBankAccounts = sortExportBankAccounts(bankAccounts, isExport)
  return (
    <FormField hideMessage helperText="Select bank account for payment details" label="Bank Account">
      <Select
        disabled={isLoading}
        options={[
          { value: '', label: '-- No bank details --' },
          ...sortedBankAccounts.map((acc) => ({ value: `bank:${acc.id}`, label: buildBankLabel(acc, isExport) })),
          ...bankGLAccounts.map((acc) => ({ value: `gl:${acc.id}`, label: `${acc.code} — ${acc.name}` })),
        ]}
        value={selectedBankAccountId}
        onChange={(value) => setSelectedBankAccountId(value)}
      />
    </FormField>
  )
}

interface NotesRowProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
}

function NotesRow({ form, isLoading }: NotesRowProps): JSX.Element {
  const { notes, setNotes, termsConditions, setTermsConditions } = form
  return (
    <div className="flex flex-col el-gap-field el-mt-sm">
      <FormField hideMessage label="Notes">
        <Textarea
          disabled={isLoading}
          placeholder="Additional notes for the invoice..."
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormField>
      <FormField hideMessage label="Terms & Conditions">
        <Textarea
          disabled={isLoading}
          placeholder="Terms and conditions..."
          rows={3}
          value={termsConditions}
          onChange={(e) => setTermsConditions(e.target.value)}
        />
      </FormField>
    </div>
  )
}

export function InvoiceFormAdditional({
  form,
  isVendor,
  isLoading,
}: InvoiceFormAdditionalProps): JSX.Element {
  const isExport = form.invoiceType === 'export'
  return (
    <div className="card overflow-visible">
      <h3 className="text-xs font-bold tracking-wider uppercase el-px-md py-2 bg-primary-solid text-primary-foreground rounded-t">
        Additional Information
      </h3>
      <div className="el-p-md">
        <div className="form-grid el-gap-field">
          <PaymentTermsRow form={form} isLoading={isLoading} />
          {!isVendor && <BankAccountSelect form={form} isExport={isExport} isLoading={isLoading} />}
        </div>
        <NotesRow form={form} isLoading={isLoading} />
      </div>
    </div>
  )
}
